import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { formValueSelector } from 'redux-form';
import { toastr } from 'react-redux-toastr';
import { Button } from 'reactstrap';
import { ITINERARY_FOR_SCHEDULING_PATH } from '../../../../../config/paths';
import { breadcrumbsPropTypes } from '../../../../common/resource/proptypes/CommonPropTypes';
import ItinerarySearchForm from '../ItinerarySearchForm';
import { INT_DATE_FORMAT } from '../../../../../config/locale';
import {
  searchItineraryBetweenDates,
  clearItineraries,
  putAssignBusses,
  postItinerarySuggestions,
  clearItinerarySuggestions,
} from '../../../../../actions';
import { DEFAULT_QUERY_GET_ALL } from '../../../../../config/queries';
import {
  BUSES_WITHOUT_TRIP_CIRCUIT_STATUS_ERROR_MESSAGE,
  DATA_NOT_REGISTERED_ERROR_MESSAGE,
} from '../../../../../config/messages';
import Content from '../../../../layout/Content';
import { tzNormalizeDate } from '../../../../../utils/date';
import ItinerariesForSchedulingTable from './ItinerariesForSchedulingTable';
import { hadResponseSuccessWithErrorCode } from '../../../../../utils/error-handlers';
import { orderBy } from '../../../../../utils/array';

const selector = formValueSelector('ItinerarySearchForm');

const ItinerariesForScheduling = ({
  dispatchPutAssignBusses,
  dispatchClearItineraries,
  dispatchSearchItineraryBetweenDates,
  breadcrumbs,
  itineraryList,
  fromDateStringValue,
  toDateStringValue,
  dispatchPostItinerarySuggestions,
  dispatchClearItinerarySuggestions,
}) => {
  const [searchValues, setSearchValues] = useState({
    sourceLocationId: 1,
    destinationLocationId: 1,
    fromDateString: tzNormalizeDate({ format: INT_DATE_FORMAT }),
    toDateString: tzNormalizeDate({ format: INT_DATE_FORMAT }),
    ...DEFAULT_QUERY_GET_ALL,
  });

  const selectedDataRef = useRef([]);

  const searchItinerary = (searchFormValuesx) => {
    dispatchClearItineraries();
    dispatchSearchItineraryBetweenDates(searchFormValuesx);
  };

  const onSubmitToSearch = (formValues) => {
    const values = {
      sourceLocationId: formValues.sourceLocationId
        ? formValues.sourceLocationId.value
        : null,
      destinationLocationId: formValues.destinationLocationId
        ? formValues.destinationLocationId.value
        : null,
      fromDateString: tzNormalizeDate({
        date: formValues.fromDateString,
        format: INT_DATE_FORMAT,
      }),
      toDateString: tzNormalizeDate({
        date: formValues.toDateString,
        format: INT_DATE_FORMAT,
      }),
      size: DEFAULT_QUERY_GET_ALL.size,
    };
    if (formValues.serviceType) {
      values.serviceTypeId = formValues.serviceType.value;
    }
    setSearchValues(values);
    searchItinerary(values);
    dispatchClearItinerarySuggestions();
  };

  const validateValues = (formValues) => {
    let valid = true;
    formValues.forEach(({ companyBusId, tripCircuitStatus, cabinCrewList }) => {
      if (!companyBusId) valid = false;
      if (!tripCircuitStatus) valid = false;
      if (!companyBusId && !tripCircuitStatus) valid = true;
      if (!!companyBusId && cabinCrewList) valid = true;
    });
    return valid;
  };

  const onClickBusAssignment = async (formValues) => {
    const valid = validateValues(formValues);

    if (!valid) {
      toastr.error('Error', BUSES_WITHOUT_TRIP_CIRCUIT_STATUS_ERROR_MESSAGE);
      return;
    }
    const registerFormValues = formValues.map(
      ({ companyBusId, tripCircuitStatus, id, cabinCrewList }) => ({
        registeredBusId: companyBusId && companyBusId.value,
        tripCircuitStatus: tripCircuitStatus && tripCircuitStatus.value,
        itineraryId: id,
        cabinCrewIdList: cabinCrewList.map((item) => item.id),
      }),
    );

    const response = await dispatchPutAssignBusses(registerFormValues);
    const error = hadResponseSuccessWithErrorCode(response);
    if (error) toastr.error('Error', DATA_NOT_REGISTERED_ERROR_MESSAGE);
    else searchItinerary(searchValues);
  };

  const today = tzNormalizeDate();
  const tomorrow = tzNormalizeDate({ addTime: { amount: 1, unit: 'd' } });

  const subtitle = (
    <span>
      Busca los itinerarios que deseas programar, selecciona los buses y
      asígnalos hacliendo click en <strong>Asignar buses</strong>
    </span>
  );

  const handleSuggestion = async () => {
    dispatchClearItinerarySuggestions();

    const fromDateString = tzNormalizeDate({
      date: fromDateStringValue,
      format: INT_DATE_FORMAT,
    });
    const toDateString = tzNormalizeDate({
      date: toDateStringValue,
      format: INT_DATE_FORMAT,
    });

    const newItineraryList = itineraryList
      .filter((itinerary) => itinerary.companyBusId === null)
      .filter(
        (itinerary) =>
          !selectedDataRef.current.find(
            (row) => row.id === itinerary.id && !row.highlightSuggestion,
          ),
      )
      .filter((itinerary) => itinerary.active);

    const sortedData = orderBy(newItineraryList, [
      'departuretime',
      'sourceLocationName',
      'destinationLocationName',
    ]);

    const payload = {
      fromDateString,
      toDateString,
      itineraryIdList: sortedData.map((itinerary) => itinerary.id),
    };

    dispatchPostItinerarySuggestions(payload);
  };

  const suggestionButton =
    itineraryList && itineraryList.length > 0 ? (
      <Button color="info" outline className="mx-3" onClick={handleSuggestion}>
        <i className="fa fa-lightbulb-o" /> Sugerencias de Asignación
      </Button>
    ) : null;

  const content = (
    <>
      <ItinerarySearchForm
        onSubmit={onSubmitToSearch}
        initialValues={{
          fromDateString: today,
          toDateString: tomorrow,
        }}
        sourceLocationIsRequired={false}
        destinationLocationIsRequired={false}
        searchByDateRange
        extraButtons={suggestionButton}
        filterServiceType
      />
      <ItinerariesForSchedulingTable
        onHandleBusAssignment={onClickBusAssignment}
        selectedDataRef={selectedDataRef}
      />
    </>
  );

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Itinerarios para Programar"
      subtitle={subtitle}
      content={content}
    />
  );
};

const mapStateToProps = (state) => ({
  breadcrumbs: [
    ...state.ItineraryUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Itinerarios para Programar',
      href: ITINERARY_FOR_SCHEDULING_PATH,
    },
  ],
  loading: state.ItineraryUnit.Itinerary.getIn(['all', 'loading']),
  itineraryList: state.ItineraryUnit.Itinerary.getIn(['all', 'content']).toJS()
    .content,
  fromDateStringValue: selector(state, 'fromDateString'),
  toDateStringValue: selector(state, 'toDateString'),
});

ItinerariesForScheduling.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchSearchItineraryBetweenDates: PropTypes.func.isRequired,
  dispatchClearItineraries: PropTypes.func.isRequired,
  dispatchPutAssignBusses: PropTypes.func.isRequired,
  itineraryList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  ).isRequired,
  fromDateStringValue: PropTypes.string,
  toDateStringValue: PropTypes.string,
  dispatchPostItinerarySuggestions: PropTypes.func.isRequired,
  dispatchClearItinerarySuggestions: PropTypes.func.isRequired,
};

ItinerariesForScheduling.defaultProps = {
  fromDateStringValue: null,
  toDateStringValue: null,
};

const mapDispatchToProps = {
  dispatchSearchItineraryBetweenDates: searchItineraryBetweenDates,
  dispatchClearItineraries: clearItineraries,
  dispatchPutAssignBusses: putAssignBusses,
  dispatchPostItinerarySuggestions: postItinerarySuggestions,
  dispatchClearItinerarySuggestions: clearItinerarySuggestions,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ItinerariesForScheduling);
